import { CopyOutlined, DownOutlined, UpOutlined } from '@ant-design/icons';
import { App as AntApp, Avatar, Button, Tag, Tooltip, Typography } from 'antd';
import { useState } from 'react';

import type { JobFlight } from '../../../src/types.ts';
import { airlinesOf, fmtMinutes, itineraryText, splitLegs } from '../lib/itinerary.ts';
import { fmtDay, fmtMoney, fmtTime } from '../lib/format.ts';
import { FlightItinerary } from './FlightItinerary.tsx';

interface Props {
  flight: JobFlight;
  /** Ranks this fare in its list; 0 gets the "Cheapest" tag. */
  rank?: number;
  onBook?: (flight: JobFlight) => void;
  /** Start expanded (booking review). */
  defaultOpen?: boolean;
  /** Hide the book button and price emphasis (already booking this one). */
  plain?: boolean;
}

/** One fare: a summary strip per leg, expanding to the full itinerary. */
export function FlightCard({ flight, rank, onBook, defaultOpen = false, plain = false }: Props) {
  const { message } = AntApp.useApp();
  const [open, setOpen] = useState(defaultOpen);
  const legs = splitLegs(flight);
  const airlines = airlinesOf(flight.segments ?? []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(itineraryText(flight));
      message.success('Itinerary copied.');
    } catch {
      message.error('Could not copy.');
    }
  };

  return (
    <div className={`fare${open ? ' fare--open' : ''}`}>
      <div className="fare__head" onClick={() => setOpen((v) => !v)} role="button" tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setOpen((v) => !v)}>
        <div className="fare__airlines">
          {airlines.slice(0, 3).map((a) => (
            <Tooltip key={a.code ?? a.name} title={a.name}>
              <Avatar size={32} shape="square" src={a.logo ?? undefined}>
                {(a.code ?? a.name).slice(0, 2)}
              </Avatar>
            </Tooltip>
          ))}
        </div>
        <div className="fare__legs">
          {legs.length ? (
            legs.map((leg) => (
              <div key={leg.kind} className="fare__leg">
                <span className="fare__leg-kind">{leg.kind === 'out' ? 'Out' : 'Ret'}</span>
                <span className="fare__leg-times">
                  <strong>{fmtTime(leg.departs) || '—'}</strong> {leg.from} → <strong>{fmtTime(leg.arrives) || '—'}</strong> {leg.to}
                </span>
                <span className="fare__leg-meta">
                  {fmtDay(leg.departs)}
                  {leg.totalMinutes != null ? ` · ${fmtMinutes(leg.totalMinutes)}` : ''}
                  {' · '}
                  <span className={leg.stops === 0 ? 'text-good' : ''}>
                    {leg.stops === 0 ? 'direct' : `${leg.stops} stop${leg.stops > 1 ? 's' : ''}${leg.segments.length > 1 ? ` via ${leg.segments.slice(0, -1).map((s) => s.to).join(', ')}` : ''}`}
                  </span>
                </span>
              </div>
            ))
          ) : (
            <div className="fare__leg">
              <span className="fare__leg-times">
                {flight.airline} {flight.flightNo ?? ''} · {fmtTime(flight.departTime)} → {fmtTime(flight.arriveTime)}
              </span>
            </div>
          )}
        </div>
        <div className="fare__price">
          {rank === 0 && !plain && (
            <Tag color="green" bordered={false}>
              Cheapest
            </Tag>
          )}
          {flight.totalBeforeDiscount != null && flight.totalBeforeDiscount > flight.price && (
            <span className="fare__was">{fmtMoney(flight.totalBeforeDiscount)}</span>
          )}
          <span className="fare__amount">
            {fmtMoney(flight.price)} <small>{flight.currency}</small>
          </span>
          <span className="fare__expand">{open ? <UpOutlined /> : <DownOutlined />} details</span>
        </div>
      </div>
      {open && (
        <div className="fare__body">
          <FlightItinerary flight={flight} />
          <div className="fare__actions">
            <Button size="small" icon={<CopyOutlined />} onClick={() => void copy()}>
              Copy itinerary
            </Button>
            {onBook && !plain && (
              <Button type="primary" onClick={() => onBook(flight)}>
                Book this fare
              </Button>
            )}
          </div>
        </div>
      )}
      {!open && onBook && !plain && (
        <div className="fare__quick">
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>
            {flight.segments?.length ?? 0} segment{(flight.segments?.length ?? 0) === 1 ? '' : 's'} · via {flight.provider}
          </Typography.Text>
          <Button size="small" type="primary" ghost onClick={() => onBook(flight)}>
            Book
          </Button>
        </div>
      )}
    </div>
  );
}
